import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import WebsocketClient from "../../../util/src/websocket-client";
import { Box, Typography } from "@mui/material";

interface TimerWidgetProps {
  serverUrl?: string;
  meetId?: string;
  token?: string;
}

const TimerWidget: React.FC<TimerWidgetProps> = ({
  serverUrl,
  meetId,
  token,
}) => {
  const [timer, setTimer] = useState("1:00");
  const socketRef = useRef<WebsocketClient | null>(null);

  const formatTime = (totalSeconds: number) => {
    if (totalSeconds < 60) return String(totalSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const getColorFromUrl = (): string => {
    if (typeof window === "undefined") return "#000000";
    const params = new URLSearchParams(window.location.search);
    const colorParam = params.get("color");
    return colorParam && colorParam.trim() !== "" ? colorParam : "#000000";
  };

  const color = getColorFromUrl();

  useEffect(() => {
    const onTimerDuration = (res: unknown) => {
      const data = res as string;
      setTimer(data);
    };

    const onTimerUpdate = (res: unknown) => {
      const data = res as { timeLeft: number };
      setTimer(formatTime(data.timeLeft));
    };

    const onceConnected = (wsClient: WebsocketClient) => {
      if (wsClient.isConnected()) {
        wsClient.off("timerDuration", onTimerDuration);
        wsClient.off("timerUpdate", onTimerUpdate);
        wsClient.on("timerDuration", onTimerDuration);
        wsClient.on("timerUpdate", onTimerUpdate);
        wsClient.emit(
          "requestCurrentTimer",
          meetId ? { id: meetId, auth_token: token } : undefined,
        );
      }
    };

    if (serverUrl) {
      socketRef.current = WebsocketClient.getInstance(serverUrl);

      if (!socketRef.current.isConnected()) {
        socketRef.current.connect(() => onceConnected(socketRef.current!));
      } else {
        onceConnected(socketRef.current);
      }

      return () => {
        socketRef.current?.off("timerDuration", onTimerDuration);
        socketRef.current?.off("timerUpdate", onTimerUpdate);
        socketRef.current?.disconnect();
        socketRef.current = null;
      };
    }
  }, [meetId, serverUrl, token]);

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Typography
        sx={{
          fontSize: "65vh",
          color,
        }}
      >
        {timer}
      </Typography>
    </Box>
  );
};

TimerWidget.propTypes = {
  serverUrl: PropTypes.string,
  meetId: PropTypes.string,
  token: PropTypes.string,
};

export default TimerWidget;
